import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const STATUSES = [
  { value: 'Not Started', color: '#B9B7AE' },
  { value: 'In Progress', color: '#0F0F10' },
  { value: 'Done',        color: '#1DBF73' },
  { value: 'Blocked',     color: '#E24B5B' },
];

/**
 * StatusDropdown
 *
 * Renders its menu through a portal to document.body so the menu cannot be
 * clipped by parent `overflow: hidden` containers (e.g. Dashboard task cards).
 *
 * Closes on: outside click, scroll (any ancestor), window resize, Escape key,
 * status selection. Position is recalculated from the trigger button's
 * getBoundingClientRect() each time the menu opens.
 */
const StatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const currentStatus = STATUSES.find((s) => s.value === value) || STATUSES[0];

  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = Math.max(160, rect.width);
    // Default: open below the button
    let top = rect.bottom + 4;
    let left = rect.left;
    const menuHeight = 180; // rough estimate; menu has 4 items
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    // If not enough room below, flip above
    if (top + menuHeight > viewportH - 8 && rect.top - menuHeight - 4 > 8) {
      top = rect.top - menuHeight - 4;
    }
    // Clamp horizontally
    if (left + menuWidth > viewportW - 8) {
      left = Math.max(8, viewportW - menuWidth - 8);
    }
    setCoords({ top, left, width: menuWidth });
  }, []);

  // When opening, compute initial position
  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  // Close handlers: outside click, scroll, resize, Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (e) => {
      const target = e.target;
      if (
        buttonRef.current && buttonRef.current.contains(target)
      ) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setIsOpen(false);
    };
    const handleScroll = () => setIsOpen(false);
    const handleResize = () => setIsOpen(false);
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    // capture=true so we catch scrolls from any scrollable ancestor
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const handleSelect = (val) => {
    setIsOpen(false);
    if (val !== value) onChange(val);
  };

  // Keyboard a11y on button
  const onButtonKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        className="status-pill"
        style={{ backgroundColor: currentStatus.color, color: '#ffffff', width: '100%' }}
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        type="button"
        data-testid="status-dropdown-button"
      >
        {currentStatus.value}
      </button>
      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="dropdown status-dropdown-menu"
          role="listbox"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            minWidth: coords.width,
            zIndex: 2147483600,
          }}
          data-testid="status-dropdown-menu"
        >
          {STATUSES.map((status) => (
            <div
              key={status.value}
              className="dropdown-item"
              role="option"
              aria-selected={status.value === value}
              onClick={() => handleSelect(status.value)}
              data-testid="status-option"
            >
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '2px',
                  backgroundColor: status.color,
                }}
              />
              <span>{status.value}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default StatusDropdown;
